import { Notyf } from 'notyf';

document.addEventListener('DOMContentLoaded', () => {
    const page = document.querySelector<HTMLElement>('.travellers-list-page');
    if (!page) return;

    initSelection();
    initSortHeaders();
    initSearch();
    initLazyStats(page);
    initLazyTypeCounts(page);
    initBulkActions();
    initImportStub();
});

// ── Selection ─────────────────────────────────────────────────────────────

let selectedIds = new Set<string>();

function initSelection() {
    const checkAll  = document.getElementById('tlp-check-all');
    const tbody     = document.getElementById('tlp-tbody');
    const bulkBar   = document.getElementById('tlp-bulk-bar');
    const bulkCount = document.getElementById('tlp-bulk-count');
    const bulkClear = document.getElementById('tlp-bulk-clear');

    if (!checkAll || !tbody || !bulkBar || !bulkCount || !bulkClear) return;

    tbody.querySelectorAll<HTMLElement>('.tlp-row-check').forEach(box => {
        box.addEventListener('click', () => toggleRow(box));
        box.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); toggleRow(box); }
        });
    });

    checkAll.addEventListener('click', () => {
        const allBoxes = Array.from(tbody.querySelectorAll<HTMLElement>('.tlp-row-check'));
        const allChecked = allBoxes.every(b => b.classList.contains('checked'));
        if (allChecked) {
            allBoxes.forEach(b => { b.classList.remove('checked'); deselectRow(b); });
        } else {
            allBoxes.forEach(b => { b.classList.add('checked'); selectRow(b); });
        }
        updateBulkBar();
        checkAll.classList.toggle('checked', !allChecked);
    });
    checkAll.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); checkAll.click(); }
    });

    bulkClear.addEventListener('click', () => clearSelection());
    document.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Escape' && selectedIds.size > 0) clearSelection();
    });

    function toggleRow(box: HTMLElement) {
        const tr = box.closest('tr') as HTMLElement | null;
        if (!tr) return;
        const id = tr.dataset.id ?? '';
        if (selectedIds.has(id)) {
            selectedIds.delete(id); box.classList.remove('checked'); tr.classList.remove('tlp-tr--selected');
        } else {
            selectedIds.add(id); box.classList.add('checked'); tr.classList.add('tlp-tr--selected');
        }
        updateBulkBar();
    }

    function selectRow(box: HTMLElement) {
        const tr = box.closest('tr') as HTMLElement | null;
        if (!tr) return;
        selectedIds.add(tr.dataset.id ?? ''); tr.classList.add('tlp-tr--selected');
    }

    function deselectRow(box: HTMLElement) {
        const tr = box.closest('tr') as HTMLElement | null;
        if (!tr) return;
        selectedIds.delete(tr.dataset.id ?? ''); tr.classList.remove('tlp-tr--selected');
    }

    function updateBulkBar() {
        const n = selectedIds.size;
        bulkBar!.style.display = n > 0 ? 'flex' : 'none';
        const countText = bulkCount!.textContent?.replace(/^\d+\s*/, '') ?? '';
        bulkCount!.textContent = `${n} ${countText}`;
        const allBoxes = Array.from(tbody!.querySelectorAll<HTMLElement>('.tlp-row-check'));
        const allChecked = allBoxes.length > 0 && allBoxes.every(b => b.classList.contains('checked'));
        checkAll!.classList.toggle('checked', allChecked);
    }

    function clearSelection() {
        selectedIds.clear();
        tbody!.querySelectorAll<HTMLElement>('.tlp-row-check').forEach(b => {
            b.classList.remove('checked');
            b.closest('tr')?.classList.remove('tlp-tr--selected');
        });
        checkAll!.classList.remove('checked');
        bulkBar!.style.display = 'none';
    }
}

// ── Bulk actions ──────────────────────────────────────────────────────────

function initBulkActions() {
    const bulkBar = document.getElementById('tlp-bulk-bar');
    if (!bulkBar) return;

    const s = (window as any).__tlpStrings ?? {};
    const notyf = new Notyf({ duration: 3000 });

    bulkBar.querySelectorAll<HTMLElement>('[data-action]').forEach(btn => {
        btn.addEventListener('click', () => {
            const action = btn.dataset.action;
            if (action === 'export') {
                exportSelected();
            } else {
                notyf.open({ type: 'warning', message: s.comingSoon ?? 'Coming soon' });
            }
        });
    });

    // Row-level "message" stub
    document.querySelectorAll<HTMLElement>('[data-action="message-one"]').forEach(btn => {
        btn.addEventListener('click', () => {
            notyf.open({ type: 'warning', message: s.comingSoon ?? 'Coming soon' });
        });
    });

    document.getElementById('tlp-import-btn')?.addEventListener('click', () => {
        notyf.open({ type: 'warning', message: s.comingSoon ?? 'Coming soon' });
    });
}

function initImportStub() {
    // handled inside initBulkActions to avoid double binding; kept as named stub for clarity
}

// ── Export ─────────────────────────────────────────────────────────────────

function exportSelected() {
    const exportUrl: string = (window as any).__tlpExportUrl ?? '/Traveller/ExportCsv';
    const filters: Record<string, string> = (window as any).__tlpCurrentFilters ?? {};
    const params = new URLSearchParams(filters);
    if (selectedIds.size > 0) {
        params.set('ids', Array.from(selectedIds).join(','));
    }
    window.location.href = `${exportUrl}?${params.toString()}`;
}

// ── Sort headers ──────────────────────────────────────────────────────────

function initSortHeaders() {
    const form      = document.getElementById('tlp-filter-form') as HTMLFormElement | null;
    const sortInput = document.getElementById('tlp-sort-order') as HTMLInputElement | null;
    const pageInput = document.getElementById('tlp-page-input') as HTMLInputElement | null;
    if (!form || !sortInput) return;

    document.querySelectorAll<HTMLElement>('.tlp-th--sortable').forEach(th => {
        th.addEventListener('click', () => {
            const key     = th.dataset.sortKey ?? '';
            const current = sortInput.value;
            const next    = current === key ? `${key}_desc` : key;
            sortInput.value = next;
            if (pageInput) pageInput.value = '0';
            form.submit();
        });
    });
}

// ── Search debounce ───────────────────────────────────────────────────────

function initSearch() {
    const form      = document.getElementById('tlp-filter-form') as HTMLFormElement | null;
    const input     = document.getElementById('tlp-search-input') as HTMLInputElement | null;
    const pageInput = document.getElementById('tlp-page-input') as HTMLInputElement | null;
    if (!form || !input) return;

    let timer: ReturnType<typeof setTimeout>;
    input.addEventListener('input', () => {
        clearTimeout(timer);
        timer = setTimeout(() => {
            if (pageInput) pageInput.value = '0';
            form.submit();
        }, 280);
    });
}

// ── Lazy stats ────────────────────────────────────────────────────────────

interface TravellerListStatsDTO {
    total: number;
    newThisMonth: number;
    active: number;
    upcoming: number;
    foreign: number;
    foreignPercent: number;
    passportExpiringSoon: number;
    passportExpired: number;
}

async function fetchJSON<T>(url: string): Promise<T | null> {
    try {
        const r = await fetch(url, { headers: { Accept: 'application/json' } });
        const j = await r.json();
        return j.success ? (j.data as T) : null;
    } catch { return null; }
}

function initLazyStats(page: HTMLElement) {
    const url = page.dataset.urlStats ?? '';
    const s   = (window as any).__tlpStrings ?? {};
    loadStats(url, s);
}

async function loadStats(url: string, s: Record<string, string>) {
    const grid = document.getElementById('tlp-stats-grid');
    if (!grid || !url) return;

    const data = await fetchJSON<TravellerListStatsDTO>(url);
    if (!data) return;

    grid.innerHTML = `
        <div class="tlp-stat-card">
            <div class="tlp-stat-label">
                <div class="tlp-stat-icon" style="background:var(--tlp-accent-light);color:var(--tlp-accent)">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                </div>
                ${s.totalTravellers ?? ''}
            </div>
            <div class="tlp-stat-value">${data.total}</div>
            <div class="tlp-stat-foot">
                <span class="tlp-stat-delta up">
                    <svg width="9" height="9" viewBox="0 0 10 10" fill="none"><path d="M5 2L8 7H2L5 2Z" fill="currentColor"/></svg>
                    +${data.newThisMonth}
                </span>
                <span>${s.newThisMonth ?? ''}</span>
            </div>
            <svg class="tlp-stat-spark" width="120" height="42" viewBox="0 0 120 42" fill="none" preserveAspectRatio="none">
                <path d="M0 30 L15 24 L30 28 L45 18 L60 22 L75 12 L90 16 L105 6 L120 10 L120 42 L0 42 Z" fill="#F5A623" opacity="0.10"/>
                <path d="M0 30 L15 24 L30 28 L45 18 L60 22 L75 12 L90 16 L105 6 L120 10" stroke="#F5A623" stroke-width="1.6" fill="none"/>
            </svg>
        </div>
        <div class="tlp-stat-card">
            <div class="tlp-stat-label">
                <div class="tlp-stat-icon" style="background:var(--tlp-success-light);color:var(--tlp-success)">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="7" r="3"/><path d="M5 22l2-8h10l2 8M9 14v-1a3 3 0 016 0v1"/></svg>
                </div>
                ${s.activityLabel ?? ''}
            </div>
            <div class="tlp-stat-value">${data.active + data.upcoming}</div>
            <div class="tlp-stat-foot">
                <span>${(s.activityFoot ?? '').replace('{0}', String(data.active)).replace('{1}', String(data.upcoming))}</span>
            </div>
            <svg class="tlp-stat-spark" width="120" height="42" viewBox="0 0 120 42" fill="none" preserveAspectRatio="none">
                <path d="M0 32 L20 26 L40 30 L60 20 L80 14 L100 8 L120 12 L120 42 L0 42 Z" fill="#05A677" opacity="0.10"/>
                <path d="M0 32 L20 26 L40 30 L60 20 L80 14 L100 8 L120 12" stroke="#05A677" stroke-width="1.6" fill="none"/>
            </svg>
        </div>
        <div class="tlp-stat-card">
            <div class="tlp-stat-label">
                <div class="tlp-stat-icon" style="background:var(--tlp-info-light);color:var(--tlp-info)">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>
                </div>
                ${s.foreignLabel ?? ''}
            </div>
            <div class="tlp-stat-value">${data.foreign}</div>
            <div class="tlp-stat-foot">
                <span class="tlp-stat-delta up">
                    <svg width="9" height="9" viewBox="0 0 10 10" fill="none"><path d="M5 2L8 7H2L5 2Z" fill="currentColor"/></svg>
                    ${data.foreignPercent}%
                </span>
                <span>${s.foreignFoot ?? ''}</span>
            </div>
            <svg class="tlp-stat-spark" width="120" height="42" viewBox="0 0 120 42" fill="none" preserveAspectRatio="none">
                <path d="M0 28 L20 22 L40 26 L60 18 L80 12 L100 16 L120 10 L120 42 L0 42 Z" fill="#1A56DB" opacity="0.10"/>
                <path d="M0 28 L20 22 L40 26 L60 18 L80 12 L100 16 L120 10" stroke="#1A56DB" stroke-width="1.6" fill="none"/>
            </svg>
        </div>
        <div class="tlp-stat-card">
            <div class="tlp-stat-label">
                <div class="tlp-stat-icon" style="background:var(--tlp-warning-light);color:var(--tlp-warning)">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 3h12a4 4 0 014 4v13a1 1 0 01-1 1H6a2 2 0 01-2-2V3z"/><circle cx="12" cy="11" r="3"/><path d="M9 17h6"/></svg>
                </div>
                ${s.passportLabel ?? ''}
            </div>
            <div class="tlp-stat-value">${data.passportExpiringSoon}</div>
            <div class="tlp-stat-foot">
                ${data.passportExpired > 0
                    ? `<span class="tlp-stat-delta down"><svg width="9" height="9" viewBox="0 0 10 10" fill="none" style="transform:rotate(180deg)"><path d="M5 2L8 7H2L5 2Z" fill="currentColor"/></svg>${data.passportExpired}</span><span>${s.passportExpired ?? ''}</span>`
                    : `<span>${s.passportExpiring ?? ''}</span>`}
            </div>
            <svg class="tlp-stat-spark" width="120" height="42" viewBox="0 0 120 42" fill="none" preserveAspectRatio="none">
                <path d="M0 20 L15 26 L30 22 L45 28 L60 16 L75 20 L90 10 L105 14 L120 8 L120 42 L0 42 Z" fill="#F5A623" opacity="0.10"/>
                <path d="M0 20 L15 26 L30 22 L45 28 L60 16 L75 20 L90 10 L105 14 L120 8" stroke="#F5A623" stroke-width="1.6" fill="none"/>
            </svg>
        </div>`;
}

// ── Lazy type counts ──────────────────────────────────────────────────────

function initLazyTypeCounts(page: HTMLElement) {
    const url = page.dataset.urlTypeCounts ?? '';
    if (!url) return;
    loadTypeCounts(url);
}

async function loadTypeCounts(url: string) {
    const data = await fetchJSON<Record<string, number>>(url);
    if (!data) return;

    document.querySelectorAll<HTMLElement>('.tlp-seg__count[data-type-key]').forEach(el => {
        const key = el.dataset.typeKey ?? '';
        const n   = data[key] ?? 0;
        el.textContent = n > 0 ? String(n) : '';
    });
}
